'use client';
import { useCallback } from 'react';
import { AppDownload } from '../interfaces/domain/ow-app.modal';
import { useTranslation } from './use-translation';
import { useInstalledExtensions } from './use-installed-extensions';

// Computes a single download button's label. Only a row that targets its own
// installable app switches its verb to "launch", e.g. "Download standalone" ->
// "Launch standalone" (or the generic launch label when the original has no
// "download" word). Non-installed downloads are unchanged.
//
// A launch requires an app to launch, which means the row must carry its own
// `appId`. Plain file downloads (a .dmg/.deb/.AppImage with only a `url` and no
// `appId`) are artifacts, not installable apps — they can never be "launched",
// so they keep their original label even when the page's main app is installed.
// That's why the install check keys off `dl.appId` and does NOT fall back to the
// main app id (doing so made every URL-only row inherit the main app's installed
// state, e.g. "Launch on Mac").
//
// `fallbackIsElectron` mirrors the click handler's `dl.isElectron ?? item.isElectron`
// so a row that carries an `appId` but no explicit `isElectron` is checked
// against the right installed set.
export const useDownloadLabel = (): ((
  dl: AppDownload | undefined,
  fallbackAppId: string,
  fallbackIsElectron: boolean,
) => string) => {
  const { t } = useTranslation();
  const { installedAppIds, installedElectronAppIds } = useInstalledExtensions();

  return useCallback(
    (dl, _fallbackAppId, fallbackIsElectron) => {
      const original = dl?.label ?? '';
      if (!dl?.appId) {
        return original;
      }

      const appId = dl.appId;
      const isElectron = dl.isElectron ?? fallbackIsElectron;
      const installed = isElectron
        ? installedElectronAppIds.has(appId)
        : installedAppIds.has(appId);

      if (!installed) {
        return original;
      }

      return /download/i.test(original)
        ? original.replace(/download/i, t('launch'))
        : t('launchApp');
    },
    [installedAppIds, installedElectronAppIds, t],
  );
};
